import { useTranslation } from 'react-i18next';
import { useState, useMemo, useEffect, useDeferredValue } from 'react';
import {
    Button,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Input,
    InputGroup,
    InputLeftElement,
    Text,
    Badge,

    Box,
    useToast
} from '@chakra-ui/react';
import { AppDialog, DialogButton } from '../common/AppDialog';
import { Search } from 'lucide-react';
import { useCommandManager, Command } from '../../state/commandStore';
import { RevitShortcutImportButton } from './RevitShortcutImportButton';
import { SketchupShortcutImportButton } from './SketchupShortcutImportButton';
import { RhinoOptionsImportButton } from './RhinoOptionsImportButton';

interface ShortcutEditorProps {
    isOpen: boolean;
    onClose: () => void;
}

/** Longest plain sequence (e.g. "RECT") accepted while recording. */
const MAX_SEQUENCE_LENGTH = 6;

/** Rows committed on the frame the table first appears (first-paint budget). */
const INITIAL_ROWS = 60;

/** Rows appended per frame while the table streams the remaining rows in. */
const ROWS_PER_CHUNK = 60;

/** Effective (user-assigned or default) shortcut label of a command. */
const effectiveShortcut = (cmd: Command) =>
    (cmd.shortcut ?? cmd.defaultShortcut ?? '').trim();

export function ShortcutEditor({ isOpen, onClose }: ShortcutEditorProps) {
    const { t } = useTranslation();
    const commands = useCommandManager((s) => s.commands);
    const updateShortcut = useCommandManager((s) => s.updateShortcut);
    const resetShortcuts = useCommandManager((s) =>s.resetShortcuts); const [searchQuery, setSearchQuery] = useState(''); const [editingId, setEditingId] = useState<string | null>(null);
    const [tempShortcut, setTempShortcut] = useState('');
    // Two-phase mount: the modal shell paints first, the command table
    // follows on the next frame so opening never blocks the modal animation.
    const [showRows, setShowRows] = useState(false);
    // Progressive fill: ~300 registered commands would block the main thread
    // for hundreds of ms if committed at once. The table starts with a small
    // chunk and streams the rest in frame-sized slices.
    const [visibleCount, setVisibleCount] = useState(0);
    const toast = useToast();

    useEffect(() => {
        if (!isOpen) {
            // Panel closed: wipe transient state so the next open is fresh.
            setSearchQuery('');
            setEditingId(null);
            setTempShortcut('');
            setShowRows(false);
            setVisibleCount(0);
            return;
        }
        const raf = requestAnimationFrame(() => {
            setShowRows(true);
            setVisibleCount(INITIAL_ROWS);
        });
        return () => cancelAnimationFrame(raf);
    }, [isOpen]);

    const deferredQuery = useDeferredValue(searchQuery);
    const filteredCommands = useMemo(() => {
        const q = deferredQuery.toLowerCase();
        return Object.values(commands).filter(cmd =>
            cmd.label.toLowerCase().includes(q) ||
            cmd.category.toLowerCase().includes(q) ||
            effectiveShortcut(cmd).toLowerCase().includes(q)
        ).sort((a, b) => a.category.localeCompare(b.category) || a.label.localeCompare(b.label));
    }, [commands, deferredQuery]);

    // A (deferred) search change re-filters the list: restart the stream
    // from the top so typing stays responsive on full-table searches.
    useEffect(() => {
        setVisibleCount(INITIAL_ROWS);
    }, [deferredQuery]);

    // Streaming loop: append one chunk per animation frame until the whole
    // (filtered) list is rendered.
    useEffect(() => {
        if (!showRows || visibleCount >= filteredCommands.length) return;
        const raf = requestAnimationFrame(() => {
            setVisibleCount((count) => Math.min(count + ROWS_PER_CHUNK, filteredCommands.length));
        });
        return () => cancelAnimationFrame(raf);
    }, [showRows, visibleCount, filteredCommands.length]);

    const handleRecordKeyDown = (e: React.KeyboardEvent, cmdId: string) => {
        e.preventDefault();
        e.stopPropagation();

        // Modifier-only presses are just the start of a combination.
        if (['Control', 'Alt', 'Shift', 'Meta'].includes(e.key)) return;

        if (e.key === 'Escape') {
            setEditingId(null);
            setTempShortcut('');
            return;
        }

        // Modifier combination (Ctrl+S, Ctrl+Shift+S, ...): save immediately.
        if (e.ctrlKey || e.altKey || e.metaKey) {
            const parts: string[] = [];
            if (e.ctrlKey) parts.push('Ctrl');
            if (e.altKey) parts.push('Alt');
            if (e.shiftKey) parts.push('Shift');
            if (e.metaKey) parts.push('Meta');
            parts.push(e.key.toUpperCase());
            validateAndSave(cmdId, parts.join('+'));
            return;
        }

        // Space or Enter confirms the captured plain sequence.
        if (e.key === ' ' || e.key === 'Enter') {
            if (tempShortcut) validateAndSave(cmdId, tempShortcut);
            return;
        }

        if (e.key === 'Backspace') {
            if (tempShortcut) {
                setTempShortcut((v) => v.slice(0, -1));
            } else {
                validateAndSave(cmdId, '');
            }
            return;
        }

        if (e.key === 'Delete') {
            validateAndSave(cmdId, '');
            return;
        }

        // Named keys (F1-F12) bind directly.
        if (/^F\d{1,2}$/.test(e.key)) {
            validateAndSave(cmdId, e.key.toUpperCase());
            return;
        }

        // Plain single characters accumulate into a BIM-style sequence
        // (type "W" then "A" for "WA"; a lone "T" is valid too).
        if (/^[a-zA-Z0-9]$/.test(e.key)) {
            if (tempShortcut.length < MAX_SEQUENCE_LENGTH) {
                setTempShortcut((v) => v + e.key.toUpperCase());
            }
        }
    };

    const handleInputBlur = (cmdId: string) => {
        if (editingId === cmdId) {
            validateAndSave(cmdId, tempShortcut);
        }
    };

    const validateAndSave = (cmdId: string, shortcut: string) => {
        const key = shortcut.trim().toUpperCase();

        // Check for conflicts against effective (user or default) shortcuts.
        const conflict = key
            ? Object.values(commands).find(c =>
                c.id !== cmdId && effectiveShortcut(c).toUpperCase() === key
            )
            : undefined;
        if (conflict) {
            toast({
                title: "Shortcut Conflict",
                description: `Shortcut '${key}' is already assigned to '${conflict.label}'.`,
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
            // Optional: Ask to overwrite? For now, just reject.
            return;
        }

        updateShortcut(cmdId, key);
        setEditingId(null);
        setTempShortcut('');
    };

    const startEditing = (cmd: Command) => {
        setEditingId(cmd.id);
        setTempShortcut(effectiveShortcut(cmd));
    };

    return (
        <AppDialog
            isOpen={isOpen}
            onClose={onClose}
            size="xl"
            title={t('dialogs.ui.keyboardShortcuts')}
            footerLeft={
                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                    <RevitShortcutImportButton />
                    <SketchupShortcutImportButton />
                    <RhinoOptionsImportButton />
                </Box>
            }
            footer={
                <>
                    <DialogButton variant="ghost" onClick={resetShortcuts}>{t('dialogs.ui.resetAll')}</DialogButton>
                    <DialogButton variant="primary" onClick={onClose}>{t('dialogs.ui.done')}</DialogButton>
                </>
            }
        >
                    <InputGroup mb={4}>
                        <InputLeftElement pointerEvents="none">
                            <Search color="var(--color-text-secondary)" />
                        </InputLeftElement>
                        <Input
                            placeholder={t('dialogs.ui.searchCommands')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            bg="bg.canvas"
                            borderColor="border.base"
                            _focus={{ borderColor: 'brand.500', boxShadow: 'none' }}
                        />
                    </InputGroup>

                    <Box overflowY="auto" maxH="60vh">
                        <Table variant="simple" size="sm">
                            <Thead position="sticky" top={0} bg="bg.panel" zIndex={1}>
                                <Tr>
                                    <Th color="text.secondary" borderColor="border.base">{t('dialogs.ui.command')}</Th>
                                    <Th color="text.secondary" borderColor="border.base">{t('dialogs.ui.category')}</Th>
                                    <Th color="text.secondary" borderColor="border.base">{t('dialogs.ui.shortcut')}</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {!showRows ? (
                                    <Tr>
                                        <Td colSpan={3}>
                                            <Box h="40vh" />
                                        </Td>
                                    </Tr>
                                ) : filteredCommands.slice(0, visibleCount).map(cmd => (
                                    <Tr key={cmd.id} _hover={{ bg: 'bg.hover' }}>
                                        <Td>
                                            <Text fontWeight="medium">{cmd.label}</Text>
                                            <Text fontSize="xs" color="text.secondary">{cmd.description}</Text>
                                        </Td>
                                        <Td>
                                            <Badge colorScheme="brand">{cmd.category}</Badge>
                                        </Td>
                                        <Td width="150px">
                                            {editingId === cmd.id ? (
                                                <Input
                                                    size="sm"
                                                    value={tempShortcut}
                                                    readOnly
                                                    onBlur={() => handleInputBlur(cmd.id)}
                                                    onKeyDown={(e) => handleRecordKeyDown(e, cmd.id)}
                                                    autoFocus
                                                    placeholder={t('dialogs.ui.pressKeys')}
                                                    bg="bg.canvas"
                                                    borderColor="brand.500"
                                                    _focus={{ borderColor: 'brand.500', boxShadow: 'none' }}
                                                />
                                            ) : (
                                                <Button
                                                    size="xs"
                                                    variant="ghost"
                                                    color="text.primary"
                                                    _hover={{ bg: 'bg.hover' }}
                                                    onClick={() => startEditing(cmd)}
                                                    w="100%"
                                                    justifyContent="flex-start"
                                                >
                                                    {(() => {
                                                        const effective = effectiveShortcut(cmd);
                                                        return effective
                                                            ? effective
                                                            : <Text as="span" color="text.secondary">{t('dialogs.ui.none')}</Text>;
                                                    })()}
                                                </Button>
                                            )}
                                        </Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </Box>
        </AppDialog>
    );
}
